import { rollMissionLoadout } from './rollMissionLoadout.mjs';
import challengeConfig from '../config/challengeConfig';
import { ethers } from 'ethers';
import { abis, addresses } from '@my-app/contracts';

export function getVersionForBlock(blockNumber) {
  const versionInfo = challengeConfig.versions.find(v => blockNumber >= v.start && blockNumber <= v.stop);
  return versionInfo ? versionInfo.version : null;
}

async function getChallengeStats(provider, blockNumber, userAddress, fetchUserDetails) {
  const multicall = new ethers.Contract(addresses.multicall3, abis.Multicall3, provider);
  const beachheadInterface = new ethers.utils.Interface(abis.Beachhead);
  const beachheadJudgeInterface = new ethers.utils.Interface(abis.BeachheadJudge);
  const beachheadTokenInterface = new ethers.utils.Interface(abis.BeachheadToken);

  const initialCalls = [
    {
      name: 'getUserList',
      target: addresses.beachhead,
      allowFailure: true,
      callData: beachheadInterface.encodeFunctionData('getUserList', [blockNumber])
    },
    {
      name: 'getPassList',
      target: addresses.beachhead,
      allowFailure: true,
      callData: beachheadInterface.encodeFunctionData('getPassList', [blockNumber])
    },
    {
      name: 'getFailList',
      target: addresses.beachhead,
      allowFailure: true,
      callData: beachheadInterface.encodeFunctionData('getFailList', [blockNumber])
    },
    {
      name: 'getChallenge',
      target: addresses.beachhead,
      allowFailure: true,
      callData: beachheadInterface.encodeFunctionData('getChallenge', [blockNumber])
    },
    {
      name: 'duration',
      target: addresses.beachhead,
      allowFailure: true,
      callData: beachheadInterface.encodeFunctionData('duration')
    },
    {
      name: 'getUserProof',
      target: addresses.beachhead,
      allowFailure: true,
      callData: beachheadInterface.encodeFunctionData('getUserProof', [blockNumber, userAddress])
    },
    {
      name: 'getUserChallenge',
      target: addresses.beachhead,
      allowFailure: true,
      callData: beachheadInterface.encodeFunctionData('getUserChallenge', [blockNumber, userAddress])
    },
    {
      name: 'getVotes',
      target: addresses.beachheadJudge,
      allowFailure: true,
      callData: beachheadJudgeInterface.encodeFunctionData('getVotes', [blockNumber, userAddress])
    },
    {
      name: 'balances',
      target: addresses.beachheadJudge,
      allowFailure: true,
      callData: beachheadJudgeInterface.encodeFunctionData('balances', [userAddress])
    },
    {
      name: 'balanceOf',
      target: addresses.beachheadToken,
      allowFailure: true,
      callData: beachheadTokenInterface.encodeFunctionData('balanceOf', [userAddress])
    }
  ];

  const initialResults = await multicall.callStatic.aggregate3(initialCalls);

  const initialDecodedResults = initialResults.map(({ success, returnData }, i) => {
    if (!success) throw new Error(`Call failed for ${initialCalls[i].name}`);

    if (initialCalls[i].target === addresses.beachhead) {
      return beachheadInterface.decodeFunctionResult(initialCalls[i].name, returnData);
    } else if (initialCalls[i].target === addresses.beachheadJudge) {
      return beachheadJudgeInterface.decodeFunctionResult(initialCalls[i].name, returnData);
    } else if (initialCalls[i].target === addresses.beachheadToken) {
      return beachheadTokenInterface.decodeFunctionResult(initialCalls[i].name, returnData);
    } else {
      throw new Error(`Unknown target for call ${initialCalls[i].name}`);
    }
  });

  const userList = initialDecodedResults[0][0];
  const passList = initialDecodedResults[1][0];
  const failList = initialDecodedResults[2][0];
  const expireBlock = initialDecodedResults[3][0].toNumber();
  const total = initialDecodedResults[3][1];
  const pot = initialDecodedResults[3][2];
  const duration = initialDecodedResults[4][0].toNumber();
  const userReported = initialDecodedResults[5][0];
  const proofSuccess = initialDecodedResults[5][1];
  const proofURL = initialDecodedResults[5][2];
  const userTag = initialDecodedResults[6][0];
  const userURL = initialDecodedResults[6][1];
  const userBalance = initialDecodedResults[6][2];
  const userState = initialDecodedResults[6][3];
  const votesPass = initialDecodedResults[7][0];
  const votesFail = initialDecodedResults[7][1];
  const judgeBalance = initialDecodedResults[8][0];
  const userTokenBalance = initialDecodedResults[9][0];
  const judgmentExpireBlock = expireBlock + duration;

  const users = {};

  if (fetchUserDetails) {
    const userCalls = userList.flatMap((user) => [
      {
        name: 'getUserProof',
        target: addresses.beachhead,
        allowFailure: true,
        callData: beachheadInterface.encodeFunctionData('getUserProof', [blockNumber, user])
      },
      {
        name: 'getUserChallenge',
        target: addresses.beachhead,
        allowFailure: true,
        callData: beachheadInterface.encodeFunctionData('getUserChallenge', [blockNumber, user])
      },
      {
        name: 'getVotes',
        target: addresses.beachheadJudge,
        allowFailure: true,
        callData: beachheadJudgeInterface.encodeFunctionData('getVotes', [blockNumber, user])
      },
      {
        name: 'balances',
        target: addresses.beachheadJudge,
        allowFailure: true,
        callData: beachheadJudgeInterface.encodeFunctionData('balances', [user])
      },
      {
        name: 'getJudgeVote',
        target: addresses.beachheadJudge,
        allowFailure: true,
        callData: beachheadJudgeInterface.encodeFunctionData('getJudgeVote', [userAddress, blockNumber, user])
      },
      {
        name: 'balanceOf',
        target: addresses.beachheadToken,
        allowFailure: true,
        callData: beachheadTokenInterface.encodeFunctionData('balanceOf', [user])
      }
    ]);

    const userResults = await multicall.callStatic.aggregate3(userCalls);

    const userDecodedResults = userResults.map(({ success, returnData }, i) => {
      if (!success) throw new Error(`Call failed for ${userCalls[i].name}`);

      if (userCalls[i].target === addresses.beachhead) {
        return beachheadInterface.decodeFunctionResult(userCalls[i].name, returnData);
      } else if (userCalls[i].target === addresses.beachheadJudge) {
        return beachheadJudgeInterface.decodeFunctionResult(userCalls[i].name, returnData);
      } else if (userCalls[i].target === addresses.beachheadToken) {
        return beachheadTokenInterface.decodeFunctionResult(userCalls[i].name, returnData);
      } else {
        throw new Error(`Unknown target for call ${userCalls[i].name}`);
      }
    });

    for (let i = 0; i < userList.length; i++) {
      users[userList[i]] = {
        proof: {
          userReported: userDecodedResults[i * 6][0],
          proofSuccess: userDecodedResults[i * 6][1],
          proofURL: userDecodedResults[i * 6][2]
        },
        challenge: {
          tag: userDecodedResults[i * 6 + 1][0],
          url: userDecodedResults[i * 6 + 1][1],
          balance: userDecodedResults[i * 6 + 1][2],
          state: userDecodedResults[i * 6 + 1][3]
        },
        votes: {
          pass: userDecodedResults[i * 6 + 2][0],
          fail: userDecodedResults[i * 6 + 2][1]
        },
        judge: {
          balance: userDecodedResults[i * 6 + 3][0],
          voteSuccess: userDecodedResults[i * 6 + 4][0],
          voteWeight: userDecodedResults[i * 6 + 4][1]
        },
        tokenBalance: userDecodedResults[i * 6 + 5][0]
      };
    }
  }

  return {
    userList,
    passList,
    failList,
    participants: userList.length,
    passCount: passList.length,
    failCount: failList.length,
    total,
    pot,
    duration,
    expireBlock,
    judgmentExpireBlock,
    userReported,
    proofSuccess,
    proofURL,
    userTag,
    userURL,
    userBalance,
    userState,
    votesPass,
    votesFail,
    judgeBalance,
    userTokenBalance,
    users
  };
}

export async function rollChallenge(provider, blockNumber, userAddress, fetchUserDetails = false) {
  const block = await provider.getBlock(blockNumber);

  const version = getVersionForBlock(blockNumber);
  if (!version) {
    throw new Error(`No version found for block number ${blockNumber}`);
  }

  const currentBlock = await provider.getBlockNumber();

  const stats = await getChallengeStats(provider, blockNumber, userAddress, fetchUserDetails);

  let phase;
  if (currentBlock > stats.judgmentExpireBlock) {
    phase = 'Settlement';
  } else if (currentBlock > stats.expireBlock && currentBlock <= stats.judgmentExpireBlock) {
    phase = 'Judgement';
  } else if (currentBlock <= stats.expireBlock && currentBlock >= blockNumber) {
    phase = 'Active';
  } else {
    phase = 'Upcoming';
  }

  const missionData = {
    id: blockNumber,
    timestamp: 0,
    version: version,
    currentBlock: currentBlock,
    eta: stats.duration,
    phase: phase,
    stats: {
      ...stats
    }
  };

  if (block === null) {
    return missionData;
  } else {
    missionData.timestamp = block.timestamp;
  }

  const challengeConfigVersion = challengeConfig[version];
  const rolled = rollMissionLoadout(version, block.hash, challengeConfigVersion);
  return {
    ...missionData,
    mission: rolled.mission,
    loadout: rolled.loadout,
    success: rolled.success,
  };
}

export async function findChallenge(provider, startBlock, maxBlocksToSearch, attributes, userAddress = ethers.constants.AddressZero) {
  // Validate inputs
  if (!provider || !startBlock || !maxBlocksToSearch || !attributes) {
    throw new Error('Missing required parameters for findChallenge');
  }

  // Helper function to check if a challenge matches the desired attributes
  const matchesAttributes = (challenge, attrs) => {
    // Check mission attributes
    if (attrs.mission) {
      for (const [key, value] of Object.entries(attrs.mission)) {
        if (challenge.mission[key] !== value) {
          return false;
        }
      }
    }

    // Check loadout attributes
    if (attrs.loadout) {
      for (const [key, value] of Object.entries(attrs.loadout)) {
        if (challenge.loadout[key] !== value) {
          return false;
        }
      }
    }

    // Check success attributes
    if (attrs.success) {
      for (const [key, value] of Object.entries(attrs.success)) {
        if (challenge.success[key] !== value) {
          return false;
        }
      }
    }

    return true;
  };

  // Search backwards from startBlock
  for (let i = 0; i < maxBlocksToSearch; i++) {
    const currentBlock = startBlock - i;

    try {
      const challenge = await rollChallenge(provider, currentBlock, userAddress);

      // Skip if challenge is null or doesn't have required properties
      if (!challenge || !challenge.mission || !challenge.loadout || !challenge.success) {
        continue;
      }

      if (matchesAttributes(challenge, attributes)) {
        return challenge;
      }
    } catch (error) {
      console.error(`Error checking block ${currentBlock}:`, error);
      continue;
    }
  }

  return null; // Return null if no matching challenge is found
}
